import React from 'react';
import { handleManualInquiry } from '../utils';
import { Instagram, Twitter, Phone, Mail, MapPin } from 'lucide-react';

interface FooterProps {
  onOpenOrders: () => void;
  onOpenPolicy: (type: 'terms' | 'privacy' | 'refund' | 'shipping') => void;
  onOpenContact: () => void;
}

const Footer: React.FC<FooterProps> = ({ onOpenOrders, onOpenPolicy, onOpenContact }) => {
  return (
    <>
    <footer id="footer" className="bg-neutral-950 text-white py-24 border-t border-white/5 relative z-10">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">

          {/* Brand + Contact Column */}
          <div className="lg:col-span-1">
            <h3 className="text-4xl font-black mb-4 tracking-tighter italic">
              YUME<span className="text-amber-500">CAV</span>
            </h3>
            <p className="text-neutral-400 max-w-sm mb-6 text-sm leading-relaxed">
              Default is death. We create aesthetic upgrades and digital artifacts for the new generation.
            </p>

            {/* Contact Details */}
            <div className="space-y-3 mb-8">
              <a
                href="tel:+917896409312"
                className="flex items-center gap-3 text-neutral-300 hover:text-amber-400 transition-colors group"
              >
                <span className="p-2 bg-neutral-900 rounded-lg group-hover:bg-amber-600/20 transition-colors">
                  <Phone className="w-3.5 h-3.5" />
                </span>
                <span className="text-sm font-medium">7896409312</span>
              </a>
              <a
                href="mailto:contact@yumecav.in"
                className="flex items-center gap-3 text-neutral-300 hover:text-amber-400 transition-colors group"
              >
                <span className="p-2 bg-neutral-900 rounded-lg group-hover:bg-amber-600/20 transition-colors">
                  <Mail className="w-3.5 h-3.5" />
                </span>
                <span className="text-sm font-medium">contact@yumecav.in</span>
              </a>
              <div className="flex items-center gap-3 text-neutral-400">
                <span className="p-2 bg-neutral-900 rounded-lg">
                  <MapPin className="w-3.5 h-3.5" />
                </span>
                <span className="text-sm font-medium">Guwahati, Assam</span>
              </div>
            </div>

            {/* Social Media */}
            <div className="flex items-center gap-3">
               <a href="https://www.instagram.com/yumecav/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="p-3 bg-neutral-900 rounded-full hover:bg-amber-600 transition-colors">
                  <Instagram className="w-5 h-5" />
               </a>
               <a href="#" aria-label="Twitter" className="p-3 bg-neutral-900 rounded-full hover:bg-amber-600 transition-colors">
                  <Twitter className="w-5 h-5" />
               </a>
            </div>
          </div>

          {/* Shop Links */}
          <div className="md:justify-self-center">
            <h4 className="font-bold uppercase tracking-widest text-xs mb-6 text-neutral-400">Archive</h4>
            <ul className="space-y-4">
              <li><a href="#collection" className="text-neutral-300 hover:text-amber-400 transition-colors text-sm font-medium">Wall Art</a></li>
              <li><a href="#stickers" className="text-neutral-300 hover:text-amber-400 transition-colors text-sm font-medium">Stickers & Skins</a></li>
              <li><a href="#drops" className="text-neutral-300 hover:text-amber-400 transition-colors text-sm font-medium">Limited Drops</a></li>
            </ul>
          </div>

          {/* Policies */}
          <div className="md:justify-self-center">
            <h4 className="font-bold uppercase tracking-widest text-xs mb-6 text-neutral-400">Policies</h4>
            <ul className="space-y-4">
              <li>
                <button
                  onClick={() => onOpenPolicy('terms')}
                  className="text-neutral-300 hover:text-amber-400 transition-colors text-sm font-medium text-left"
                >
                  Terms & Conditions
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenPolicy('privacy')}
                  className="text-neutral-300 hover:text-amber-400 transition-colors text-sm font-medium text-left"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenPolicy('refund')}
                  className="text-neutral-300 hover:text-amber-400 transition-colors text-sm font-medium text-left"
                >
                  Refund Policy
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenPolicy('shipping')}
                  className="text-neutral-300 hover:text-amber-400 transition-colors text-sm font-medium text-left"
                >
                  Shipping Policy
                </button>
              </li>
            </ul>
          </div>

          {/* Support / Account */}
          <div className="md:justify-self-center">
            <h4 className="font-bold uppercase tracking-widest text-xs mb-6 text-neutral-400">Support</h4>
            <ul className="space-y-4">
              <li>
                <button
                  onClick={onOpenOrders}
                  className="text-neutral-300 hover:text-amber-400 transition-colors text-sm font-medium text-left"
                >
                  Track Your Order
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleManualInquiry("Bulk B2B Inquiry")}
                  className="text-neutral-300 hover:text-amber-400 transition-colors text-sm font-medium text-left"
                >
                  B2B Pricing
                </button>
              </li>
              <li>
                <button
                  onClick={onOpenContact}
                  className="text-neutral-300 hover:text-amber-400 transition-colors text-sm font-medium text-left"
                >
                  Contact Us
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-neutral-500 font-mono gap-4">
          <p>&copy; {new Date().getFullYear()} YUMECAV. ALL RIGHTS RESERVED.</p>
          <div className="flex gap-4">
             <button onClick={() => onOpenPolicy('privacy')} className="hover:text-white transition-colors uppercase tracking-wider">Privacy</button>
             <button onClick={() => onOpenPolicy('terms')} className="hover:text-white transition-colors uppercase tracking-wider">Terms</button>
             <button onClick={() => onOpenPolicy('refund')} className="hover:text-white transition-colors uppercase tracking-wider">Refund</button>
          </div>
        </div>
      </div>
    </footer>

      {/* Disclaimer Banner */}
      <div className="bg-gradient-to-r from-amber-900 via-amber-800 to-amber-900 py-3 px-6 text-center relative z-10">
        <p className="text-[10px] md:text-xs text-amber-100/90 leading-relaxed max-w-5xl mx-auto">
          All artwork posted on this website is intended as fan art and is not purported to be official merchandise unless indicated otherwise. 
          If you have any issues regarding the artwork, please write to us at{' '}
          <a href="mailto:contact@yumecav.in" className="underline font-bold text-white hover:text-amber-200 transition-colors">
            contact@yumecav.in
          </a>.
        </p>
      </div>
    </>
  );
};

export default Footer;
